import { DateTime } from "luxon";
import { OUTSIDE_RANGE, RangeRole } from "../calendar/calendar-styles";
import { CaptureBucket } from "../capture-date-stats";

/** A row of the capture timeline: a month holding captures, or one of its days */
export type TimelineRow = {
  date: DateTime;
  count: number;
  isMonth: boolean;
};

/**
 * @returns The timeline's rows, newest first, each month's row ahead of its days
 * @param days Days holding captures, oldest first
 */
export function timelineRows(days: CaptureBucket[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let month: TimelineRow | undefined;
  for (const { start, count } of [...days].reverse()) {
    const date = DateTime.fromJSDate(start);
    if (!month?.date.hasSame(date, "month")) {
      month = { date: date.startOf("month"), count: 0, isMonth: true };
      rows.push(month);
    }
    month.count += count;
    rows.push({ date, count, isMonth: false });
  }
  return rows;
}

/**
 * The range reads as one block down the timeline, its ends being the top and bottom row
 * inside it. A month row counts as inside when the range runs on past the month, or ends
 * with the month and holds all of it.
 *
 * @returns How each row relates to the active range
 * @param rows The timeline's rows, in order
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function timelineRoles(
  rows: TimelineRow[],
  start?: Date,
  end?: Date,
): RangeRole[] {
  const isInside = rows.map(({ date, isMonth }) => {
    if (!start || !end) return false;
    const from = date.toJSDate();
    const to = date.endOf(isMonth ? "month" : "day").toJSDate();
    if (!isMonth) return to >= start && from <= end;
    return end > to
      ? start <= to
      : end.getTime() === to.getTime() && start <= from;
  });
  const top = isInside.indexOf(true);
  const bottom = isInside.lastIndexOf(true);
  return isInside.map((isSelected, index) =>
    isSelected
      ? {
          isSelected,
          isRangeStart: index === bottom,
          isRangeEnd: index === top,
        }
      : OUTSIDE_RANGE,
  );
}
